import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppImage } from "@/src/components/NextImage";
import { Drawer, DrawerProps } from "antd";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { DRAG_CLOSE_ANIM, useDraggable } from "./useDraggable";
import {
  DrawerCanonicalScrollContext,
  type DrawerCanonicalScrollContextValue,
} from "./drawerCanonicalScrollContext";
import { resolveDrawerVerticalTopOffsetPx } from "./drawerVerticalTopOffset";
import { useIsPc, useSafeAreaInsets } from "@hooks";
import { useMemoizedFn } from "ahooks";

export interface EdgenDrawerProps extends DrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  titleClassName?: string;
  contentClassName?: string;
  children?: React.ReactNode;
  width?: number | string;
  showBgShadow?: boolean;
  className?: string;
  /**
   * 点击内容滚动区域的空白处（非 children 区域）时关闭 Drawer。
   * 仅当点击目标是内容容器自身（e.target === e.currentTarget）才会触发，避免影响 children 内部交互。
   */
  closeOnContentBlankClick?: boolean;
  styles?: {
    mask?: CSSProperties;
    wrapper?: CSSProperties;
    content?: CSSProperties;
    body?: CSSProperties;
    header?: CSSProperties;
  };
  zIndex?: number;
  isMask?: boolean;
  isHiddenClose?: boolean;
  /** 是否支持拖拽关闭 */
  draggable?: boolean;
  /**
   * 垂直抽屉顶部留白（px）。H5 在 `--safe-area-inset-top` 为 0 时作为兜底；
   * 原生已注入安全区时由 safe area 取代，不与 initDistance 叠加。
   */
  initDistance?: number;
  /** 拖拽关闭的距离阈值（px），默认 200 */
  dragThreshold?: number;
}

/** 拖拽松手后尺寸回位：ease-out-back，略过冲，模拟弹簧回弹（纯 CSS） */
const DRAG_RELEASE_EASING = "cubic-bezier(0.175, 0.885, 0.32, 1.275)";
const DRAG_RELEASE_DURATION_S = 0.42;

const EdgenDrawer: React.FC<EdgenDrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width,
  className,
  titleClassName,
  contentClassName,
  closeOnContentBlankClick = false,
  styles,
  zIndex = 1000,
  placement = "right",
  isMask = true,
  isHiddenClose = false,
  draggable = false,
  initDistance = 0,
  dragThreshold = 200,
  destroyOnClose = true,
  showBgShadow = false,
  ...otherProps
}) => {
  const [isPc] = useIsPc();
  const { top: safeTop } = useSafeAreaInsets();
  const contentRef = useRef<HTMLDivElement>(null);
  const canonicalScrollRootRef = useRef<HTMLElement | null>(null);
  const [canonicalScrollRootVersion, setCanonicalScrollRootVersion] =
    useState(0);
  const [showShadow, setShowShadow] = useState(false);
  // 使用防抖定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isVertical = placement === "top" || placement === "bottom";
  const isHorizontal = placement === "left" || placement === "right";

  const canonicalCoordination = draggable && placement === "bottom";

  const registerCanonicalScrollRoot = useMemoizedFn(
    (el: HTMLElement | null) => {
      canonicalScrollRootRef.current = el;
      setCanonicalScrollRootVersion((v) => v + 1);
    },
  );

  const listPullDownFromScrollRoot = useMemo(
    () =>
      canonicalCoordination && isVertical
        ? {
            getScrollRoot: () => canonicalScrollRootRef.current,
            dependencyKey: canonicalScrollRootVersion,
          }
        : undefined,
    [canonicalCoordination, isVertical, canonicalScrollRootVersion],
  );

  const canonicalCtxValue = useMemo(
    (): DrawerCanonicalScrollContextValue =>
      canonicalCoordination && isVertical
        ? { mode: "active", registerCanonicalScrollRoot }
        : { mode: "inactive" },
    [canonicalCoordination, isVertical, registerCanonicalScrollRoot],
  );

  // 使用拖拽功能 - 通过 enabled 参数控制是否启用
  const {
    isDragging,
    isDragCloseAnimating,
    dragDistance,
    handleMouseDown,
    handleTouchStart,
    handleDragBarClick,
  } = useDraggable({
    enabled: draggable,
    onClose,
    threshold: dragThreshold,
    isOpen,
    listPullDownFromScrollRoot,
  });

  // 计算抽屉宽度和样式
  const drawerWidth = width || (isPc ? 560 : "100%");

  const verticalTopOffsetPx = useMemo(
    () =>
      resolveDrawerVerticalTopOffsetPx({
        isPc,
        isVertical,
        safeTop,
        initDistance,
      }),
    [isPc, isVertical, safeTop, initDistance],
  );

  // 垂直抽屉在 H5 端接近满屏：顶部扣减 safe-area-inset-top 或 initDistance（二选一）
  // 用 Drawer 的 height 承载基础高度，避免覆写 wrapper 高度导致进场动画生硬
  // const drawerHeight =
  //   otherProps.height ??
  //   (isVertical ? `calc(100% - ${verticalTopOffsetPx}px)` : undefined);

  const drawerHeight = otherProps.height ?? (isVertical ? "100%" : undefined);

  // 计算抽屉尺寸（根据拖动距离调整）
  const dynamicHeight = isVertical
    ? `calc(100% - ${verticalTopOffsetPx}px - ${dragDistance}px)`
    : undefined;
  const dynamicWidth = isHorizontal
    ? `calc(100% - ${dragDistance}px)`
    : undefined;
  const shouldApplyDynamicSize =
    isDragging || isDragCloseAnimating || dragDistance > 0;

  const mergedStyles: DrawerProps["styles"] = {
    mask: {
      backgroundColor: ospThemes.colors.background_clearcover,
      ...styles?.mask,
    },
    wrapper: {
      // 应用默认样式
      ...(isPc && {
        margin: "16px 16px 16px 0",
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: ospThemes.colors.background_solid_secondary,
      }),
      borderRadius: isPc ? "16px" : "16px 16px 0 0",
      // 应用用户传入的样式
      ...styles?.wrapper,
    },
    content: {
      backgroundColor: ospThemes.colors.background_solid_secondary,
      borderRadius: isPc ? "16px" : "16px 16px 0 0",
      ...styles?.content,
    },
    body: {
      padding: "24px 12px 0 12px",
      backgroundColor: ospThemes.colors.background_solid_secondary,
      height: "100%",
      ...styles?.body,
    },
    // 如果开启拖拽，应用动态尺寸和过渡效果（确保不被覆盖）
    ...(draggable && {
      wrapper: {
        ...(shouldApplyDynamicSize &&
          dynamicHeight && { height: dynamicHeight }),
        ...(shouldApplyDynamicSize && dynamicWidth && { width: dynamicWidth }),
        ...(shouldApplyDynamicSize && {
          transition: isDragging
            ? "none"
            : isDragCloseAnimating
              ? isVertical
                ? `height ${DRAG_CLOSE_ANIM.durationS}s ${DRAG_CLOSE_ANIM.easing}`
                : `width ${DRAG_CLOSE_ANIM.durationS}s ${DRAG_CLOSE_ANIM.easing}`
              : isVertical
                ? `height ${DRAG_RELEASE_DURATION_S}s ${DRAG_RELEASE_EASING}`
                : `width ${DRAG_RELEASE_DURATION_S}s ${DRAG_RELEASE_EASING}`,
        }),
      },
      body: {
        paddingTop: 8,
        ...styles?.body,
      },
    }),
  };

  // 处理滚动事件，更新蒙层状态
  const handleScroll = useCallback(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 添加50ms的防抖
    debounceTimerRef.current = setTimeout(() => {
      if (contentRef.current) {
        const container = contentRef.current;
        // 当滚动位置大于5px时，开始显示蒙层
        const scrollThreshold = 5;
        // 添加滚动方向判断
        if (container.scrollTop > scrollThreshold) {
          setShowShadow(true);
        } else {
          setShowShadow(false);
        }
      }
    }, 10);
  }, []);

  const handleContentClick = useMemoizedFn((e) => {
    if (!closeOnContentBlankClick) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  });

  useEffect(() => {
    if (!isMask) {
      return;
    }
    const container = contentRef.current;
    if (!isOpen) {
      container?.removeEventListener("scroll", handleScroll);
      return;
    }
    if (container) {
      container.addEventListener("scroll", handleScroll);

      // 初始检查
      handleScroll();
      return () => {
        if (debounceTimerRef?.current) {
          clearTimeout(debounceTimerRef.current);
        }
        container?.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, isOpen, isMask]);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      placement={placement}
      width={drawerWidth}
      height={drawerHeight}
      closable={false}
      classNames={{
        content: cn("relative", className),
      }}
      styles={mergedStyles}
      zIndex={zIndex}
      destroyOnClose={destroyOnClose} // 控制关闭时是否销毁内容
      {...otherProps}
    >
      <DrawerCanonicalScrollContext.Provider value={canonicalCtxValue}>
        {/* 拖动条 - 仅在开启拖拽功能时显示 */}
        {draggable && (
          <div
            data-openreplay-label="Drawer · div"
            className="w-[80px] h-3 m-auto cursor-pointer px-5 select-none absolute z-11 pt-2 pb-[20px] left-1/2"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onClick={handleDragBarClick}
            style={{
              touchAction: "none",
              transform: "translateX(-50%)",
            }}
          >
            <div className="w-[40px] h-1 bg-background_white_overlay10 rounded-lg"></div>
          </div>
        )}
        {showBgShadow && (
          <>
            <AppImage
              src="/assets/images/chat/chat_left_bg.webp"
              alt="chat left background"
              width={324}
              height={313}
              className="absolute left-0 top-0 w-[324px] h-[313px] object-cover opacity-60"
            />
            <AppImage
              src="/assets/images/chat/chat_right_bg.webp"
              alt="chat right background"
              width={305}
              height={518}
              className="absolute w-[305px] h-[518px] object-fill right-0 top-0 opacity-60"
            />
          </>
        )}
        {isMask && (
          <div
            className={cn(
              "absolute top-25 left-0 w-full h-10 bg-gradient-to-b from-background_solid_secondary 10% to-background_solid_secondary/0 90%",
            )}
            style={{
              opacity: showShadow ? 1 : 0,
            }}
          ></div>
        )}

        <div className="flex flex-col h-full">
          <div
            className={cn("block", {
              hidden: isHiddenClose,
            })}
          >
            <Icon
              data-openreplay-label="close"
              name="close"
              size={24}
              color={
                isPc
                  ? ospThemes.colors.background_white_overlay20
                  : ospThemes.colors.content_tertiary
              }
              className={cn("cursor-pointer absolute top-3 right-3 p-1 z-100")}
              onClick={onClose}
            />
            <div
              className={cn(
                "headline_regular_l p-3 mb-4 text-content_primary",
                titleClassName,
              )}
            >
              {title}
            </div>
          </div>
          <div
            className={cn("flex-grow h-full pb-12", contentClassName)}
            ref={contentRef}
            onClick={handleContentClick}
          >
            {children}
          </div>
        </div>
        {isMask && (
          <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-t from-background_solid_secondary 10% to-background_solid_secondary/0 90%"></div>
        )}
      </DrawerCanonicalScrollContext.Provider>
    </Drawer>
  );
};

export default EdgenDrawer;
